// System
import { useDispatch } from 'react-redux';
import { TablePagination } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
// Components
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';
// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';
// Table
import { useFailedSendCreditTransactionsTableColumns } from './useFailedSendCreditTransactionsTableColumns';
// Types
import { FailedSendCreditTrasnactionItemDto } from '@contracts/api/dto/failedSendCreditTransaction/FailedSendCreditTransactionItemDto';
// Redux
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
// Graphql
import { useFailedSendCreditTransactionsTable } from '@Modal/hooks/failedSendCreditTransactionsTable.ts/useFailedSendCreditTransactionsTable';
import { useFailedSendCreditTransactionsQuery } from '@Src/generated.graphql';

export const FailedSendCreditTransactionsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    columnVisibility,
    visiblityCustomizable,
    failedSendCreditTransactionsListFilters,
    total,
    page,
    limit,
    failedSendCreditTransactionsTableFilters,
    setPage,
    setLimit,
    setTotal,
  } = useFailedSendCreditTransactionsTable();
  const { userPermissions, currentUser } = useAuthorization();
  const columns = useFailedSendCreditTransactionsTableColumns();

  const permissionToViewFailedSendCreditTasks =
    userPermissions?.includes('CAN_VIEW_FAILED_SEND_CREDIT_TRANSACTIONS') || currentUser.isSuper;

  const { data, loading, refetch } = useFailedSendCreditTransactionsQuery({
    variables: {
      limit,
      page,
      filter: failedSendCreditTransactionsTableFilters,
    },
    skip: !permissionToViewFailedSendCreditTasks,
  });
  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, failedSendCreditTransactionsListFilters),
    [columns, columnVisibility]
  );

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  useEffect(() => {
    if (data?.aviatorFailedProviderSendCreditTransactions) {
      const { total, limit, page } = data.aviatorFailedProviderSendCreditTransactions;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewFailedSendCreditTasks) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }}>
        <TableEx name="aviatorFailedSendCreditTasks">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.aviatorFailedProviderSendCreditTransactions?.docs?.map(
              // @ts-ignore
              (row: FailedSendCreditTrasnactionItemDto) => (
                <TableRowPresenter
                  id={row._id}
                  key={row._id}
                  columns={visibleColumns}
                  row={row}
                  onClick={() => {}}
                />
              )
            )}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableEx>
    </>
  );
};
